/* eslint-disable no-empty */
/* eslint-disable max-len */
import { fetchM } from '../actions/fetchM';

// In-memory cache
const apiCache = {};
const LS_PREFIX = 'apiCache:'; // prefix for localStorage keys

// Custom error for API failures
export class FetchError extends Error {
  constructor(res, data = null) {
    super(`Request failed: ${res.status} ${res.statusText}`);
    this.name = 'FetchError';
    this.res = res;
    this.data = data;
  }
}

// Helpers for localStorage cache
function saveToLocalStorage(key, { body, status, statusText, headers, timestamp, size }) {
  try {
    localStorage.setItem(
      LS_PREFIX + key,
      JSON.stringify({ body, status, statusText, headers, timestamp, size }),
    );
  } catch (err) {
    console.warn('LocalStorage save failed', err);
  }
}

function loadFromLocalStorage(key) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    // reconstruct Response
    const res = new Response(parsed.body, {
      status: parsed.status,
      statusText: parsed.statusText,
      headers: parsed.headers,
    });

    return {
      res,
      timestamp: parsed.timestamp,
      size: parsed.size,
    };
  } catch (err) {
    console.warn('LocalStorage load failed', err);
    return null;
  }
}

function removeFromLocalStorage(key) {
  try {
    localStorage.removeItem(LS_PREFIX + key);
  } catch (err) {
    console.warn('LocalStorage remove failed', err);
  }
}

// Cache utilities
function cleanupCache(ttl, source = 'both') {
  const now = Date.now();

  // Memory cleanup
  if (source === 'both' || source === 'memory') {
    Object.keys(apiCache).forEach((key) => {
      if (now - apiCache[key].timestamp > ttl) {
        delete apiCache[key];
        if (source === 'both') removeFromLocalStorage(key); // sync delete
      }
    });
  }

  // LocalStorage cleanup
  if (source === 'both' || source === 'localStorage') {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) {
        try {
          const key = k.slice(LS_PREFIX.length);
          const value = localStorage.getItem(k);
          if (!value) {
            localStorage.removeItem(k);
          } else {
            const parsed = JSON.parse(value);
            if (parsed && now - parsed.timestamp > ttl) {
              localStorage.removeItem(k);
              if (source === 'both') delete apiCache[key]; // sync delete
            }
          }
        } catch (err) {
          localStorage.removeItem(k);
        }
      }
    }
  }
}

export function clearAllCache() {
  Object.keys(apiCache).forEach(key => delete apiCache[key]);
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith(LS_PREFIX)) {
      localStorage.removeItem(k);
    }
  }
}

export function getCacheStats(ttl = 30000, source = 'both') {
  const now = Date.now();
  let stats = [];

  // Memory cache stats
  if (source === 'both' || source === 'memory') {
    const memStats = Object.entries(apiCache).map(([key, { timestamp, size, res }]) => {
      const ageMs = now - timestamp;
      return {
        key,
        source: 'memory',
        ageMs,
        ageSec: (ageMs / 1000).toFixed(1),
        expired: ageMs > ttl,
        fromCache: true,
        isStale: ageMs > ttl,
        status: (res && res.status) || 'N/A',
        size,
      };
    });
    stats = stats.concat(memStats);
  }

  // LocalStorage cache stats
  if (source === 'both' || source === 'localStorage') {
    const lsStats = Object.keys(localStorage)
      .filter(k => k.startsWith(LS_PREFIX))
      .map((k) => {
        try {
          const key = k.replace(LS_PREFIX, '');
          const parsed = JSON.parse(localStorage.getItem(k));
          const ageMs = now - parsed.timestamp;
          return {
            key,
            source: 'localStorage',
            ageMs,
            ageSec: (ageMs / 1000).toFixed(1),
            expired: ageMs > ttl,
            fromCache: true,
            isStale: ageMs > ttl,
            status: parsed.status || 'N/A',
            size: parsed.size || (parsed.body ? parsed.body.length : 0),
          };
        } catch (err) {
          return null;
        }
      })
      .filter(Boolean);

    stats = stats.concat(lsStats);
  }

  if (stats.length > 0) {
    console.table(stats);
  } else {
    console.log(`Cache is empty (${source})`);
  }
  return stats;
}

function normalizeUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    if (!u.search) return u.toString();

    const params = new URLSearchParams(u.search);
    const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
    u.search = new URLSearchParams(sorted).toString();

    return u.toString();
  } catch (err) {
    return url;
  }
}

async function fetchAndCache(store, url, options, key, cacheType) {
  const res = await store.dispatch(fetchM(url, options));

  let data = null;
  try {
    const contentType = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
    if (contentType.includes('application/json')) {
      data = await res.clone().json();
    } else {
      data = await res.clone().text();
    }
  } catch (err) {
    // ignore parse errors
  }

  if (!res.ok) {
    throw new FetchError(res, data);
  }

  // Cache with size estimate
  const bodyStr = typeof data === 'string' ? data : JSON.stringify(data);
  const size = bodyStr ? bodyStr.length : 0;

  // Memory cache
  if (cacheType === 'memory' || cacheType === 'both') {
    apiCache[key] = {
      res: res.clone(),
      timestamp: Date.now(),
      size,
    };
  }

  // LocalStorage cache
  if (cacheType === 'localStorage' || cacheType === 'both') {
    saveToLocalStorage(key, {
      body: bodyStr,
      status: res.status,
      statusText: res.statusText,
      headers: Object.fromEntries(res.headers.entries()),
      timestamp: Date.now(),
      size,
    });
  }

  return res;
}

const shouldRefreshFlags = new Set();

async function refreshCache(store, url, options, key, cacheType) {
  if (shouldRefreshFlags.has(key)) return; // skip duplicate refresh
  shouldRefreshFlags.add(key);

  try {
    await fetchAndCache(store, url, options, key, cacheType);
  } catch (err) {
    console.error('Background refresh failed', err);
  } finally {
    shouldRefreshFlags.delete(key);
  }
}

/**
 * Fetch data with cache + SWR (stale-while-revalidate).
 * Modes:
 *   - "ttl"    : Use cache if fresh, else refresh in background (SWR).
 *   - "always" : Always fetch fresh, no cache at all.
 * Cache types:
 *   - "memory"       : (default) only in-memory
 *   - "localStorage" : only localStorage
 *   - "both"         : memory + localStorage
 */
export async function fetchWithCacheSWR(
  store,
  url,
  options = {},
  ttl = 30000,
  cacheKey = null,
  mode = 'ttl',
  cacheType = 'memory',
) {
  // getCacheStats(ttl, cacheType);
  cleanupCache(ttl, cacheType);

  const normalizedUrl = normalizeUrl(url);
  const key = cacheKey || normalizedUrl;
  const now = Date.now();

  // check in-memory first (if enabled)
  let cached = (cacheType === 'memory' || cacheType === 'both') ? apiCache[key] : null;

  // if not in memory, try localStorage
  if (!cached && (cacheType === 'localStorage' || cacheType === 'both')) {
    const lsCache = loadFromLocalStorage(key);
    if (lsCache) {
      cached = lsCache;
      if (cacheType === 'both') {
        apiCache[key] = lsCache;
      }
    }
  }

  if (mode === 'always') {
    // Always fetch fresh, never cache
    const res = await store.dispatch(fetchM(url, options));
    let data = null;
    try {
      data = await res.clone().json();
    } catch (e) {}
    if (!res.ok) throw new FetchError(res, data);
    return res;
  }

  // ttl mode
  if (cached) {
    const isExpired = now - cached.timestamp > ttl;
    if (!isExpired) {
      return cached.res.clone();
    }
    refreshCache(store, url, options, key, cacheType);
    return cached.res.clone();
  }

  return fetchAndCache(store, url, options, key, cacheType);
}



// WEBPACK FOOTER //
// ./src/utils/fetchWithCacheSWR.js